import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { apiFetch } from "../../../config/api";
import { getUser } from "../../../config/auth";
import { canCloseDeal, canReassignDeal, canDeleteCrmDeal } from "../../../config/rbac";
import { formatDate } from "../../../config/utils";
import EmptyState from "../../../components/EmptyState";
import Loader from "../../../components/Loader";
import PageHeader from "../../../components/PageHeader";

const STAGES = ["Lead", "Qualified", "Proposal", "Negotiation", "Won", "Lost"];

const STAGE_META = {
  Lead: { color: "#475569", bg: "#f8fafc", border: "#cbd5e1" },
  Qualified: { color: "#0b72b9", bg: "#eff6ff", border: "#bfdbfe" },
  Proposal: { color: "#b45309", bg: "#fffbeb", border: "#fde68a" },
  Negotiation: { color: "#6d28d9", bg: "#f5f3ff", border: "#ddd6fe" },
  Won: { color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0" },
  Lost: { color: "#b91c1c", bg: "#fef2f2", border: "#fecaca" },
};

const EMPTY_SUMMARY = {
  totalDeals: 0,
  openDeals: 0,
  wonDeals: 0,
  lostDeals: 0,
  openPipelineValue: 0,
  wonValue: 0,
  overdueFollowUps: 0,
  followUpsDueToday: 0,
  dealsByStage: {},
};

const EMPTY_FORM = {
  accountName: "",
  contactName: "",
  contactEmail: "",
  contactPhone: "",
  stage: "Lead",
  estimatedValue: "",
  expectedCloseDate: "",
  nextFollowUpAt: "",
  source: "",
  notes: "",
  ownerUserId: "",
};

const EMPTY_ACTIVITY_FORM = {
  type: "Note",
  summary: "",
  activityAt: "",
};

function formatMoney(value) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function toDateInput(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function isOpenStage(stage) {
  return stage !== "Won" && stage !== "Lost";
}

function isOverdueFollowUp(deal) {
  if (!deal.nextFollowUpAt || !isOpenStage(deal.stage)) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const followUp = new Date(deal.nextFollowUpAt);
  followUp.setHours(0, 0, 0, 0);
  return followUp < today;
}

function SummaryMetric({ label, value, tone }) {
  const accent =
    tone === "success" ? "var(--success)" :
    tone === "danger" ? "var(--danger)" :
    tone === "warn" ? "var(--warn)" :
    "var(--brand)";

  return (
    <div className="summary-card" style={{ borderTop: `3px solid ${accent}` }}>
      <div className="summary-card-label">{label}</div>
      <div className="summary-card-value" style={{ color: accent }}>
        {value}
      </div>
    </div>
  );
}

function StageBadge({ stage }) {
  const meta = STAGE_META[stage] || STAGE_META.Lead;
  return (
    <span
      className="crm-stage-badge"
      style={{ color: meta.color, background: meta.bg, borderColor: meta.border }}
    >
      {stage}
    </span>
  );
}

export default function PipelinePage() {
  const location = useLocation();
  const role = getUser()?.role;
  const canClose = canCloseDeal(role);
  const canReassign = canReassignDeal(role);
  const canDelete = canDeleteCrmDeal(role);

  const [deals, setDeals] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState(location.state?.search || "");
  const [stage, setStage] = useState("All");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [modalError, setModalError] = useState("");
  const [editingDeal, setEditingDeal] = useState(null);
  const [showDealModal, setShowDealModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [activityDeal, setActivityDeal] = useState(null);
  const [activityForm, setActivityForm] = useState(EMPTY_ACTIVITY_FORM);

  const loadCrm = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (stage !== "All") params.set("stage", stage);
      if (search.trim()) params.set("search", search.trim());
      const qs = params.toString() ? `?${params.toString()}` : "";

      const [summaryRes, dealsRes] = await Promise.all([
        apiFetch("/api/crm/summary"),
        apiFetch(`/api/crm/deals${qs}`),
      ]);

      if (!summaryRes.ok || !dealsRes.ok) {
        const message = await dealsRes.text();
        throw new Error(message || "Could not load CRM data.");
      }

      const [summaryData, dealData] = await Promise.all([
        summaryRes.json(),
        dealsRes.json(),
      ]);

      setSummary({ ...EMPTY_SUMMARY, ...(summaryData || {}) });
      setDeals(Array.isArray(dealData) ? dealData : []);
    } catch (err) {
      setError(err?.message || "Could not load CRM data.");
      setDeals([]);
      setSummary(EMPTY_SUMMARY);
    } finally {
      setLoading(false);
    }
  }, [search, stage]);

  useEffect(() => {
    loadCrm();
  }, [loadCrm]);

  useEffect(() => {
    apiFetch("/api/users/assignable")
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setUsers(Array.isArray(data) ? data : []))
      .catch(() => setUsers([]));
  }, []);

  const columns = useMemo(() => {
    const visibleStages = stage === "All" ? STAGES : [stage];
    return visibleStages.map((item) => ({
      stage: item,
      deals: deals.filter((deal) => deal.stage === item),
    }));
  }, [deals, stage]);

  const openCreateModal = () => {
    setEditingDeal(null);
    setForm(EMPTY_FORM);
    setModalError("");
    setShowDealModal(true);
  };

  const openEditModal = (deal) => {
    setEditingDeal(deal);
    setForm({
      accountName: deal.accountName || "",
      contactName: deal.contactName || "",
      contactEmail: deal.contactEmail || "",
      contactPhone: deal.contactPhone || "",
      stage: deal.stage || "Lead",
      estimatedValue: deal.estimatedValue ?? "",
      expectedCloseDate: toDateInput(deal.expectedCloseDate),
      nextFollowUpAt: toDateInput(deal.nextFollowUpAt),
      source: deal.source || "",
      notes: deal.notes || "",
      ownerUserId: deal.ownerUserId || "",
    });
    setModalError("");
    setShowDealModal(true);
  };

  const updateForm = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const closeDealModal = () => {
    setShowDealModal(false);
    setEditingDeal(null);
    setModalError("");
    setForm(EMPTY_FORM);
  };

  const submitDeal = async () => {
    if (saving) return;
    if (!form.accountName.trim() || !form.contactName.trim()) {
      setModalError("Account name and contact name are required.");
      return;
    }

    setSaving(true);
    setModalError("");
    try {
      const payload = {
        accountName: form.accountName.trim(),
        contactName: form.contactName.trim(),
        contactEmail: form.contactEmail.trim() || null,
        contactPhone: form.contactPhone.trim() || null,
        stage: form.stage,
        estimatedValue: Number(form.estimatedValue || 0),
        expectedCloseDate: form.expectedCloseDate || null,
        nextFollowUpAt: form.nextFollowUpAt || null,
        source: form.source.trim() || null,
        notes: form.notes.trim() || null,
        ownerUserId: form.ownerUserId || null,
      };

      const res = await apiFetch(
        editingDeal ? `/api/crm/deals/${editingDeal.id}` : "/api/crm/deals",
        {
          method: editingDeal ? "PUT" : "POST",
          body: JSON.stringify(payload),
        },
      );

      if (!res.ok) {
        const message = await res.text();
        throw new Error(message || "Could not save CRM deal.");
      }

      closeDealModal();
      await loadCrm();
    } catch (err) {
      setModalError(err?.message || "Could not save CRM deal.");
    } finally {
      setSaving(false);
    }
  };

  const openActivityModal = (deal) => {
    setActivityDeal(deal);
    setActivityForm(EMPTY_ACTIVITY_FORM);
    setModalError("");
  };

  const closeActivityModal = () => {
    setActivityDeal(null);
    setActivityForm(EMPTY_ACTIVITY_FORM);
    setModalError("");
  };

  const deleteDeal = async (deal) => {
    if (!window.confirm(`Permanently delete "${deal.accountName}"? This cannot be undone.`)) return;
    try {
      const res = await apiFetch(`/api/crm/deals/${deal.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await res.text());
      await loadCrm();
    } catch (err) {
      setError(err?.message || "Could not delete deal.");
    }
  };

  const submitActivity = async () => {
    if (!activityDeal || saving) return;
    if (!activityForm.summary.trim()) {
      setModalError("Activity summary is required.");
      return;
    }

    setSaving(true);
    setModalError("");
    try {
      const res = await apiFetch(`/api/crm/deals/${activityDeal.id}/activities`, {
        method: "POST",
        body: JSON.stringify({
          type: activityForm.type,
          summary: activityForm.summary.trim(),
          activityAt: activityForm.activityAt || null,
        }),
      });

      if (!res.ok) {
        const message = await res.text();
        throw new Error(message || "Could not save activity.");
      }

      closeActivityModal();
      await loadCrm();
    } catch (err) {
      setModalError(err?.message || "Could not save activity.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="crm-page">
      <PageHeader
        title="Pipeline"
        subtitle="Track accounts, contacts, pipeline value, and follow-ups from lead to close."
        actions={
          <button className="btn" onClick={openCreateModal}>
            New Deal
          </button>
        }
      />

      <div className="summary-grid">
        <SummaryMetric label="Total Deals" value={summary.totalDeals} />
        <SummaryMetric label="Open Pipeline" value={formatMoney(summary.openPipelineValue)} />
        <SummaryMetric label="Won Value" value={formatMoney(summary.wonValue)} tone="success" />
        <SummaryMetric label="Overdue Follow-ups" value={summary.overdueFollowUps} tone={summary.overdueFollowUps ? "danger" : "success"} />
        <SummaryMetric label="Due Today" value={summary.followUpsDueToday} tone="warn" />
      </div>

      <div className="crm-toolbar glass-card">
        <div className="crm-search">
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search account, contact, email, or source"
            aria-label="Search CRM"
          />
        </div>
        <div className="crm-stage-filter" aria-label="CRM pipeline stage">
          <button
            type="button"
            className={stage === "All" ? "active" : ""}
            onClick={() => setStage("All")}
          >
            All
            <span>{summary.totalDeals}</span>
          </button>
          {STAGES.filter((item) => canClose || (item !== "Won" && item !== "Lost")).map((item) => (
            <button
              key={item}
              type="button"
              className={stage === item ? "active" : ""}
              onClick={() => setStage(item)}
            >
              {item}
              <span>{summary.dealsByStage?.[item] || 0}</span>
            </button>
          ))}
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {loading ? (
        <div className="crm-board">
          {STAGES.slice(0, 4).map((item) => (
            <div key={item} className="crm-column">
              <Loader type="skeleton" width="45%" height={16} style={{ marginBottom: 14 }} />
              <Loader type="skeleton" height={92} style={{ marginBottom: 10 }} />
              <Loader type="skeleton" height={92} />
            </div>
          ))}
        </div>
      ) : deals.length === 0 ? (
        <div className="glass-card">
          <EmptyState
            title={search || stage !== "All" ? "No CRM deals match the current filters" : "No CRM deals yet"}
            subtitle={search || stage !== "All" ? "Clear the search or switch stage filters." : "Create the first account or lead to start the sales pipeline."}
            action={<button className="btn" onClick={openCreateModal}>New Deal</button>}
          />
        </div>
      ) : (
        <div className="crm-board">
          {columns.map((column) => {
            const meta = STAGE_META[column.stage] || STAGE_META.Lead;
            return (
              <section key={column.stage} className="crm-column">
                <div className="crm-column-header" style={{ borderColor: meta.border }}>
                  <div>
                    <div className="crm-column-title" style={{ color: meta.color }}>
                      {column.stage}
                    </div>
                    <div className="crm-column-subtitle">
                      {column.deals.length} deal{column.deals.length === 1 ? "" : "s"}
                    </div>
                  </div>
                  <span style={{ background: meta.bg, color: meta.color, borderColor: meta.border }}>
                    {summary.dealsByStage?.[column.stage] || 0}
                  </span>
                </div>

                <div className="crm-deal-list">
                  {column.deals.length === 0 ? (
                    <div className="crm-empty-column">No deals in this stage</div>
                  ) : (
                    column.deals.map((deal) => (
                      <article key={deal.id} className="crm-deal-card">
                        <div className="crm-deal-topline">
                          <div>
                            <h3>{deal.accountName}</h3>
                            <p>{deal.contactName}</p>
                          </div>
                          <StageBadge stage={deal.stage} />
                        </div>

                        <div className="crm-deal-value">{formatMoney(deal.estimatedValue)}</div>

                        <dl className="crm-deal-meta">
                          <div>
                            <dt>Owner</dt>
                            <dd>{deal.ownerUserName || "Unassigned"}</dd>
                          </div>
                          <div>
                            <dt>Follow-up</dt>
                            <dd className={isOverdueFollowUp(deal) ? "danger" : ""}>
                              {formatDate(deal.nextFollowUpAt)}
                            </dd>
                          </div>
                          <div>
                            <dt>Close Date</dt>
                            <dd>{formatDate(deal.expectedCloseDate)}</dd>
                          </div>
                          <div>
                            <dt>Source</dt>
                            <dd>{deal.source || "-"}</dd>
                          </div>
                        </dl>

                        {deal.lastActivitySummary && (
                          <div className="crm-last-activity">
                            {deal.lastActivitySummary}
                          </div>
                        )}

                        <div className="crm-card-actions">
                          <button type="button" className="btn-ghost" onClick={() => openEditModal(deal)}>
                            Edit
                          </button>
                          <button type="button" className="btn-ghost" onClick={() => openActivityModal(deal)}>
                            Activity
                          </button>
                          {canDelete && (
                            <button
                              type="button"
                              className="btn-ghost"
                              style={{ color: "var(--danger)" }}
                              onClick={() => deleteDeal(deal)}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </article>
                    ))
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {showDealModal && (
        <div className="modal-overlay" onClick={closeDealModal}>
          <div className="glass-card modal crm-modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingDeal ? "Edit CRM Deal" : "New CRM Deal"}</h3>
              <button className="modal-close" onClick={closeDealModal}>x</button>
            </div>

            {modalError && <div className="error-banner">{modalError}</div>}

            <div className="crm-form-grid">
              <div>
                <label className="form-label">Account Name</label>
                <input className="form-input" value={form.accountName} onChange={(e) => updateForm("accountName", e.target.value)} />
              </div>
              <div>
                <label className="form-label">Contact Name</label>
                <input className="form-input" value={form.contactName} onChange={(e) => updateForm("contactName", e.target.value)} />
              </div>
              <div>
                <label className="form-label">Contact Email</label>
                <input className="form-input" type="email" value={form.contactEmail} onChange={(e) => updateForm("contactEmail", e.target.value)} />
              </div>
              <div>
                <label className="form-label">Phone</label>
                <input className="form-input" value={form.contactPhone} onChange={(e) => updateForm("contactPhone", e.target.value)} />
              </div>
              <div>
                <label className="form-label">Stage</label>
                <select className="form-input" value={form.stage} onChange={(e) => updateForm("stage", e.target.value)}>
                  {STAGES.filter((item) => canClose || (item !== "Won" && item !== "Lost")).map((item) => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Estimated Value</label>
                <input className="form-input" type="number" min="0" value={form.estimatedValue} onChange={(e) => updateForm("estimatedValue", e.target.value)} />
              </div>
              <div>
                <label className="form-label">Expected Close Date</label>
                <input className="form-input" type="date" value={form.expectedCloseDate} onChange={(e) => updateForm("expectedCloseDate", e.target.value)} />
              </div>
              <div>
                <label className="form-label">Next Follow-up</label>
                <input className="form-input" type="date" value={form.nextFollowUpAt} onChange={(e) => updateForm("nextFollowUpAt", e.target.value)} />
              </div>
              <div>
                <label className="form-label">Source</label>
                <input className="form-input" value={form.source} onChange={(e) => updateForm("source", e.target.value)} />
              </div>
              {canReassign && (
                <div>
                  <label className="form-label">Owner</label>
                  <select className="form-input" value={form.ownerUserId} onChange={(e) => updateForm("ownerUserId", e.target.value)}>
                    <option value="">Current user</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name || user.email}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="crm-form-wide">
                <label className="form-label">Notes</label>
                <textarea className="form-input" rows={4} value={form.notes} onChange={(e) => updateForm("notes", e.target.value)} />
              </div>
            </div>

            <div className="modal-actions">
              <button onClick={closeDealModal} disabled={saving}>Cancel</button>
              <button className="primary" onClick={submitDeal} disabled={saving}>
                {saving && <Loader size="sm" inline />}
                {editingDeal ? "Save Changes" : "Create Deal"}
              </button>
            </div>
          </div>
        </div>
      )}

      {activityDeal && (
        <div className="modal-overlay" onClick={closeActivityModal}>
          <div className="glass-card modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Activity</h3>
              <button className="modal-close" onClick={closeActivityModal}>x</button>
            </div>

            {modalError && <div className="error-banner">{modalError}</div>}

            <div className="form-group">
              <label className="form-label">Deal</label>
              <input className="form-input" value={activityDeal.accountName} disabled />
            </div>
            <div className="form-group">
              <label className="form-label">Type</label>
              <select
                className="form-input"
                value={activityForm.type}
                onChange={(event) => setActivityForm((current) => ({ ...current, type: event.target.value }))}
              >
                {["Note", "Call", "Email", "Meeting", "Task"].map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Activity Date</label>
              <input
                className="form-input"
                type="date"
                value={activityForm.activityAt}
                onChange={(event) => setActivityForm((current) => ({ ...current, activityAt: event.target.value }))}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Summary</label>
              <textarea
                className="form-input"
                rows={4}
                value={activityForm.summary}
                onChange={(event) => setActivityForm((current) => ({ ...current, summary: event.target.value }))}
              />
            </div>

            <div className="modal-actions">
              <button onClick={closeActivityModal} disabled={saving}>Cancel</button>
              <button className="primary" onClick={submitActivity} disabled={saving}>
                {saving && <Loader size="sm" inline />}
                Save Activity
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
